import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import BaseModal, { ModalFooter } from "../base/BaseModal";
import { validateGovernmentForm } from "../../validator/governmentValidator";
import { GOVERNMENT_API } from "../../apis/government";
import { showToast } from "../../utils/toastNotification";

const initialState = {
    tenCoSo: "",
    diaChi: "",
    soDienThoai: "",
    moTa: "",
    linkGoogleMap: "",
};

const GovernmentFormModal = ({
    isOpen,
    onClose,
    onCreate,
    onSubmit,
    mode = "create",
    initialData = null,
    isLoading = false,
}) => {
    const [form, setForm] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [, setIsSubmitting] = useState(false);
    const [showMapLinkGuideModal, setShowMapLinkGuideModal] = useState(false);
    const [pastedLinkLength, setPastedLinkLength] = useState(0);

    useEffect(() => {
        if (isOpen) {
            if (mode === "edit" && initialData) {
                setForm({
                    tenCoSo: initialData.tenCoSo || initialData.ten_co_so || "",
                    diaChi: initialData.diaChi || initialData.dia_chi || "",
                    soDienThoai:
                        initialData.soDienThoai || initialData.so_dien_thoai || "",
                    moTa: initialData.moTa || initialData.mo_ta || "",
                    linkGoogleMap:
                        initialData.linkGoogleMap || initialData.link_google_map || "",
                });
            } else {
                setForm(initialState);
            }
        } else {
            setForm(initialState);
            setErrors({});
        }
    }, [isOpen, mode, initialData]);

    const updateField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (errors[key]) {
            setErrors((prev) => ({ ...prev, [key]: null }));
        }
    };

    const handleBlur = (field) => {
        if (field === 'soDienThoai') {
            const val = (form.soDienThoai || '').trim();
            if (!val) {
                setErrors(prev => ({ ...prev, soDienThoai: 'Số điện thoại là bắt buộc' }));
            } else if (!/^(03|05|07|08|09)\d{8}$|^02\d{8,9}$/.test(val)) {
                setErrors(prev => ({
                    ...prev,
                    soDienThoai: 'Số điện thoại không hợp lệ (gồm 10 số di động bắt đầu bằng 03, 05, 07, 08, 09 hoặc 10-11 số máy bàn bắt đầu bằng 02)'
                }));
            } else {
                setErrors(prev => {
                    const next = { ...prev };
                    delete next.soDienThoai;
                    return next;
                });
            }
        }
    };

    const handleGoogleMapChange = (value) => {
        updateField("linkGoogleMap", value);
        if (value && value.trim().length > 500) {
            setPastedLinkLength(value.trim().length);
            setShowMapLinkGuideModal(true);
            setErrors((prev) => ({
                ...prev,
                linkGoogleMap: "Link Google Maps vượt quá 500 ký tự. Vui lòng dùng link rút gọn từ nút Chia sẻ.",
            }));
        }
    };

    const handleGoogleMapPaste = (e) => {
        const text = e.clipboardData?.getData("text") || "";
        if (text.trim().length > 500) {
            setPastedLinkLength(text.trim().length);
            setShowMapLinkGuideModal(true);
        }
    };

    const handleSubmit = async () => {
        if (form.linkGoogleMap && form.linkGoogleMap.trim().length > 500) {
            setPastedLinkLength(form.linkGoogleMap.trim().length);
            setShowMapLinkGuideModal(true);
            setErrors((prev) => ({
                ...prev,
                linkGoogleMap: "Link Google Maps vượt quá 500 ký tự. Vui lòng dùng link rút gọn từ nút Chia sẻ.",
            }));
            showToast.error("Link Google Maps quá dài, vui lòng dùng link rút gọn từ nút Chia sẻ!");
            return;
        }

        const { isValid, errors: validationErrors } = await validateGovernmentForm(
            form
        );

        if (!isValid) {
            setErrors(validationErrors);
            showToast.error('Vui lòng kiểm tra lại các trường bắt buộc!');
            return;
        }

        const payload = {
            tenCoSo: form.tenCoSo,
            diaChi: form.diaChi,
            soDienThoai: form.soDienThoai,
            moTa: form.moTa?.trim() || null,
            linkGoogleMap: form.linkGoogleMap,
        };

        setIsSubmitting(true);
        try {
            if (onSubmit) {
                await onSubmit(payload);
                setForm(initialState);
                setErrors({});
                onClose();
            } else if (mode === "create") {
                const apiCreated = await GOVERNMENT_API.createGovernment(payload);
                if (apiCreated) {
                    if (onCreate) {
                        try {
                            onCreate(apiCreated);
                        } catch (e) {
                        }
                    }

                    setForm(initialState);
                    setErrors({});
                    onClose();
                }
            }
        } catch (err) {
            if (err?.message) {
                showToast?.error(err.message);
            } else {
                showToast?.error("Có lỗi xảy ra, vui lòng thử lại!");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title={
                mode === "edit"
                    ? "Chỉnh sửa cơ sở dịch vụ công"
                    : "Tạo mới cơ sở dịch vụ công"
            }
            size="xl"
            className="max-w-5xl"
            footer={
                <ModalFooter
                    onCancel={onClose}
                    onSubmit={handleSubmit}
                    cancelText="Hủy"
                    submitText={mode === "edit" ? "Cập nhật" : "Tạo"}
                    submitDisabled={false}
                    submitLoading={isLoading}
                />
            }
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-medium text-gray-700 required-label">
                        Tên cơ sở
                    </label>
                    <input
                        type="text"
                        value={form.tenCoSo}
                        onChange={(e) => updateField("tenCoSo", e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${errors.tenCoSo ? "border-red-500" : "border-gray-300"
                            }`}
                    />
                    {errors.tenCoSo && (
                        <p className="mt-1 text-sm text-red-600">{errors.tenCoSo}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 required-label">
                        Địa chỉ
                    </label>
                    <input
                        type="text"
                        value={form.diaChi}
                        onChange={(e) => updateField("diaChi", e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${errors.diaChi ? "border-red-500" : "border-gray-300"
                            }`}
                    />
                    {errors.diaChi && (
                        <p className="mt-1 text-sm text-red-600">{errors.diaChi}</p>
                    )}
                </div>
                <div className="md:col-span-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 required-label">
                            Số điện thoại
                        </label>
                        <input
                            type="text"
                            value={form.soDienThoai}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^[0-9]*$/.test(value)) {
                                    updateField("soDienThoai", value);
                                }
                            }}
                            onBlur={() => handleBlur('soDienThoai')}
                            placeholder="Ví dụ: 0901234567 hoặc 02838123456"
                            maxLength="11"
                            className={`w-full px-3 py-2 border rounded-lg ${errors.soDienThoai ? "border-red-500" : "border-gray-300"
                                }`}
                        />
                        {errors.soDienThoai && (
                            <p className="mt-1 text-sm text-red-600">{errors.soDienThoai}</p>
                        )}
                    </div>
                </div>

                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                        Mô tả
                    </label>
                    <textarea
                        value={form.moTa}
                        onChange={(e) => updateField("moTa", e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${errors.moTa ? "border-red-500" : "border-gray-300"
                            }`}
                    />
                    {errors.moTa && (
                        <p className="mt-1 text-sm text-red-600">{errors.moTa}</p>
                    )}
                </div>

                <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700 required-label">
                            Vị trí trên Google Maps
                        </label>
                        {form.linkGoogleMap ? (
                            <span className={`text-xs ${form.linkGoogleMap.length > 500 ? "text-red-600 font-semibold" : "text-gray-400"}`}>
                                {form.linkGoogleMap.length}/500 ký tự
                            </span>
                        ) : null}
                    </div>
                    <div className="flex items-center gap-2 w-full">
                        <input
                            type="text"
                            value={form.linkGoogleMap}
                            onChange={(e) => handleGoogleMapChange(e.target.value)}
                            onPaste={handleGoogleMapPaste}
                            placeholder="Ví dụ: https://maps.app.goo.gl/... hoặc dán link Google Maps"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                                errors.linkGoogleMap ? "border-red-500 bg-red-50/20" : "border-gray-300"
                            }`}
                        />
                    </div>
                    {errors.linkGoogleMap ? (
                        <p className="mt-1 text-sm text-red-600">{errors.linkGoogleMap}</p>
                    ) : (
                        <p className="mt-1 text-xs text-gray-500">
                            💡 <span className="font-medium text-blue-700">Gợi ý:</span> Nên dùng link rút gọn từ nút <strong>"Chia sẻ"</strong> trên Google Maps (dạng <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-700">https://maps.app.goo.gl/...</code>) để tránh link quá dài.
                        </p>
                    )}
                </div>
            </div>
        </BaseModal>

        {/* Popup thông báo & hướng dẫn khi dán link Google Maps quá dài */}
        <BaseModal
                isOpen={showMapLinkGuideModal}
                onClose={() => setShowMapLinkGuideModal(false)}
                title="Đường dẫn Google Maps quá dài"
                size="md"
                footer={
                    <div className="flex justify-end gap-2 w-full">
                        <button
                            type="button"
                            onClick={() => {
                                updateField("linkGoogleMap", "");
                                setShowMapLinkGuideModal(false);
                            }}
                            className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 font-medium rounded-lg transition-colors border border-red-200"
                        >
                            Xóa link này để dán lại
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowMapLinkGuideModal(false)}
                            className="px-4 py-2 text-sm bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Đã hiểu, tôi sẽ lấy link rút gọn
                        </button>
                    </div>
                }
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900">
                        <div className="p-1.5 bg-amber-100 rounded-full flex-shrink-0 mt-0.5 text-amber-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div className="text-sm">
                            <p className="font-semibold text-amber-900">
                                Link vừa nhập dài {pastedLinkLength || form.linkGoogleMap?.length} ký tự (hạn mức tối đa 500 ký tự)!
                            </p>
                            <p className="mt-1 text-amber-700 leading-relaxed">
                                Link copy trực tiếp từ thanh địa chỉ trình duyệt thường chứa rất nhiều tham số thừa và tọa độ phiên làm việc, dẫn đến link quá dài khiến hệ thống không lưu được.
                            </p>
                        </div>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 text-sm space-y-2.5">
                        <p className="font-semibold text-slate-800 flex items-center gap-1.5">
                            <span className="text-blue-600">📍</span> Hướng dẫn lấy Link rút gọn chuẩn:
                        </p>
                        <ol className="list-decimal list-inside space-y-2 text-slate-600 ml-1 leading-relaxed">
                            <li>
                                Mở địa điểm trên <strong className="text-slate-800">Google Maps</strong>.
                            </li>
                            <li>
                                Bấm vào nút <strong className="text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">Chia sẻ</strong> (Share) ngay dưới tên địa điểm.
                            </li>
                            <li>
                                Nhấn <strong className="text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">Sao chép liên kết</strong> (Link rút gọn sẽ có dạng ngắn gọn như <code className="text-xs bg-white px-1 py-0.5 border rounded">https://maps.app.goo.gl/...</code> chỉ từ 30 - 60 ký tự).
                            </li>
                            <li>
                                Quay lại đây và dán liên kết rút gọn vào ô.
                            </li>
                        </ol>
                    </div>
                </div>
            </BaseModal>
        </>
    );
};

GovernmentFormModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreate: PropTypes.func,
    onSubmit: PropTypes.func,
    mode: PropTypes.oneOf(["create", "edit"]),
    initialData: PropTypes.object,
    isLoading: PropTypes.bool,
};

export default GovernmentFormModal;
